// Inline dimmable-light card for the room grid — compact vertical channel
// sliders, always visible, sized to sit next to the touch/fan cards. Covers all
// three variants (DeviceID 25 / 90 W/WW, 24 R/G/B, 23 W/WW/R/G/B).
//
// Runs on core/hooks/useLightChannels like the phone screen, so a reversed
// light (DevicesInstalled.SwapWarm) is reversed here too. White / Cool White /
// Warm White swatches, On-Off and Blink on the card; ⋮ adds Blink to identify
// and Reverse white / warm white (ui/shared/controls/LightActions).

import React from 'react';
import {
  Card, Box, Typography, Slider, Button, IconButton, Tooltip,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import { useLightChannels } from '../../../core/hooks/useLightChannels';
import { deviceTypeKey } from '../../../core/devices/deviceMeta';
import { RGB_PRESETS, WWH_PRESETS, WWRGB_PRESETS } from '../../../core/devices/light';
import { LightNotice, useLightMenu } from '../../shared/controls/LightActions';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

const CHANNELS = {
  rgb: ['R', 'G', 'B'],
  wwh: ['W', 'WW'],
  wwrgb: ['W', 'WW', 'R', 'G', 'B'],
};
const PRESETS = { rgb: RGB_PRESETS, wwh: WWH_PRESETS, wwrgb: WWRGB_PRESETS };

// The card has room for the whites only; colours stay on the sliders.
// A wwh card is a sixth of the row wide, so the labels are one word each.
const WHITES = { White: 'White', 'Cool White': 'Cool', 'Warm White': 'Warm' };
const flat = (label) => label.replace('\n', ' ').trim();

function channelColor(ch) {
  const c = String(ch).toLowerCase();
  if (c === 'r') return '#e53935';
  if (c === 'g') return '#43a047';
  if (c === 'b') return '#1e88e5';
  if (c === 'w') return '#e0e0e0';
  if (c === 'ww' || c === 'wh') return '#ffb300';
  return '#90caf9';
}

export default function LightControlCard({ device, subtitle }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const key = deviceTypeKey(device.DeviceID);
  const channels = CHANNELS[key] || CHANNELS.rgb;
  // Only for the live dot — the values come from useLightChannels.
  const [, , liveAt] = useDeviceStatus(uno, parseStatus(device.Status || device.DeviceStatus || ''));
  const light = useLightChannels(uno, channels.length, userId);
  const { values, setValues, commit, sendCommand } = light;
  const menu = useLightMenu(device, light);
  const whites = (PRESETS[key] || []).filter((p) => WHITES[flat(p.label)]);

  const onChange = (i, v) => setValues((prev) => { const u = [...prev]; u[i] = v; return u; });
  const onCommit = (i, v) => { const u = [...values]; u[i] = v; commit(u); };
  const isOn = values.some((v) => v > 0);

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>{subtitle}</Typography>
          )}
        </Box>
        <Tooltip title="Blink to identify">
          <span>
            <IconButton size="small" disabled={light.blinking} onClick={light.blink} color={light.blinking ? 'warning' : 'default'}>
              <FlashOnIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu
          device={device}
          extraItems={menu.items.map((it) => ({ label: it.label, icon: it.icon, onClick: it.onClick }))}
        />
      </Box>
      {/* ⚠️ THE NAME HAS ITS OWN LINE (owner, 2026-10-20). Sharing the row with
          ⚡ · dot · ⋮ on a card this narrow cut it to "WWH ...", and two WWH lights
          side by side could not be told apart. Smaller, wraps (up to 3 lines),
          full name on hover. */}
      <Typography
        title={device.DeviceName || uno}
        fontWeight={700}
        color="text.primary"
        sx={{
          fontSize: 12.5,
          lineHeight: 1.25,
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          mt: -0.25,
        }}
      >
        {device.DeviceName || uno}
        {light.reversed && <Typography component="span" variant="caption" color="text.secondary"> · reversed</Typography>}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-around', alignItems: 'flex-end', flex: 1, pt: 0.5 }}>
        {channels.map((ch, i) => (
          <Box key={ch} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <Slider
              orientation="vertical"
              min={0} max={255} value={values[i] || 0}
              disabled={light.blinking}
              onChange={(_, v) => onChange(i, v)}
              onChangeCommitted={(_, v) => onCommit(i, v)}
              sx={{ height: 88, color: channelColor(ch), '& .MuiSlider-rail': { opacity: 0.25 }, '& .MuiSlider-thumb': { width: 13, height: 13 } }}
            />
            <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>{ch}</Typography>
          </Box>
        ))}
      </Box>

      {whites.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
          {whites.map((p) => (
            <Button
              key={p.label} size="small" variant="outlined" disabled={light.blinking}
              onClick={() => sendCommand(p.command)}
              sx={{ minWidth: 0, px: 0.75, fontSize: 11, lineHeight: 1.2, color: 'text.primary', borderColor: 'divider', flex: 1 }}
              title={flat(p.label)}
            >
              <Box component="span" sx={{ width: 9, height: 9, mr: 0.5, flexShrink: 0, borderRadius: '3px', bgcolor: p.swatch, border: 1, borderColor: 'divider' }} />
              {WHITES[flat(p.label)]}
            </Button>
          ))}
        </Box>
      )}

      <Button
        size="small" fullWidth variant={isOn ? 'contained' : 'outlined'}
        color={isOn ? 'error' : 'inherit'} disabled={light.blinking}
        startIcon={<PowerSettingsNewIcon />} onClick={isOn ? light.turnOff : light.turnOn}
        sx={{ mt: 0.5, color: isOn ? undefined : 'text.primary', borderColor: isOn ? undefined : 'divider' }}
      >
        {light.blinking ? 'Blinking…' : (isOn ? 'Off' : 'On')}
      </Button>
      <LightNotice notice={menu.notice} onClose={menu.clearNotice} />
    </Card>
  );
}
